import { isMissingTranslationColumns, localizeActivity } from "./activity-localization";
import type { SupabaseClient } from "@supabase/supabase-js";
import { readAllPages } from "./pagination";
import { deleteFile, signKey, signKeys, uploadFile } from "./files";
import {
  ACTIVITIES_PER_DAY,
  activitiesForDay,
  bandForLevel,
  enrichmentForDay,
  levelProgress,
  termActivities,
} from "./curriculum";
import type { Level, Term } from "./curriculum";
import type {
  Activity,
  ActivityResource,
  ResourceKind,
  Competency,
  CurriculumPathway,
  DeliveryMode,
  Evidence,
  EvidenceKind,
  GuardianDetails,
  Learner,
  LearnerNote,
  LearnerOverview,
  LearnerProfileInput,
  MasteryLevel,
  MasteryRow,
  PlanDay,
  PlanItem,
  PlanStatus,
  Session,
  Suggestion,
  SuggestionKind,
  SuggestionStatus,
} from "../types";

export const EVIDENCE_BUCKET = "homeschool-evidence";

/** Local calendar date as `YYYY-MM-DD`. */
export function today(date = new Date()) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function unwrap<T>(result: { data: T | null; error: { message: string } | null }): T {
  if (result.error) throw new Error(result.error.message);
  return result.data as T;
}

// ---------------------------------------------------------------------------
// Learners
// ---------------------------------------------------------------------------

const LEARNER_BASE_SELECT =
  "id,guardian_id,profile_id,name,initials,date_of_birth,stage,avatar_color,is_active," +
  "gender,home_language,city,country,about,support_needs,previous_schooling,goals," +
  "started_on,weekly_target_minutes,enrolment_status";
/** With the level columns from 20260903000000, which may not be applied yet. */
const LEARNER_LEVEL_SELECT = `${LEARNER_BASE_SELECT},level_slug,level_started_on`;
/** With the per-child service choice from 20260910000000. */
const LEARNER_SELECT =
  `${LEARNER_LEVEL_SELECT},learning_route,curriculum_pathway,preferred_delivery,supported_subjects`;

/** A learner row as it looks before the level columns exist. */
function withoutLevelFields<T extends Record<string, unknown>>(input: T) {
  const { level_slug: _slug, level_started_on: _started, ...rest } = input;
  return rest;
}

function withoutRouteFields<T extends Record<string, unknown>>(input: T) {
  const {
    learning_route: _route,
    curriculum_pathway: _pathway,
    preferred_delivery: _delivery,
    supported_subjects: _subjects,
    ...rest
  } = input;
  return rest;
}

function withLegacyRoute(learner: Learner): Learner {
  return {
    ...learner,
    learning_route: "parent_led",
    curriculum_pathway: "balanced",
    preferred_delivery: null,
    supported_subjects: [],
    pathway_blocks: null,
  };
}

export async function fetchLearners(supabase: SupabaseClient): Promise<Learner[]> {
  const read = (columns: string) =>
    supabase
      .from("homeschool_learners")
      .select(columns)
      .eq("is_active", true)
      .order("created_at", { ascending: true })
      .returns<Learner[]>();

  const modern = await read(LEARNER_SELECT);
  if (!modern.error) return unwrap(modern);
  const withLevel = await read(LEARNER_LEVEL_SELECT);
  if (!withLevel.error) return unwrap(withLevel).map(withLegacyRoute);
  // Before 20260903000000 there is no level on a learner; the app falls back to
  // the middle level of their stage rather than showing nothing.
  const legacy = unwrap(await read(LEARNER_BASE_SELECT));
  return legacy.map((learner) =>
    withLegacyRoute({ ...learner, level_slug: null, level_started_on: null } as Learner),
  );
}

export function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "AL";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : (parts[0][1] ?? "");
  return (first + last).toUpperCase();
}

/**
 * The empty profile a new child starts from.
 *
 * Everything past the name is optional on purpose: a parent who opens the app
 * at nine in the evening should be able to start tomorrow's learning tonight,
 * and fill in the rest when someone asks them for it.
 */
export function emptyLearnerProfile(): LearnerProfileInput {
  return {
    name: "",
    stage: "primary",
    // Placed from the first screen: a parent knows their child is in Nursery
    // long before they know what a "stage" is, and a child with no level gets
    // the old shuffle instead of a curriculum.
    level_slug: null,
    level_started_on: null,
    date_of_birth: null,
    avatar_color: "blue",
    gender: null,
    home_language: null,
    city: null,
    country: null,
    about: null,
    support_needs: null,
    previous_schooling: null,
    goals: null,
    started_on: null,
    weekly_target_minutes: 300,
    enrolment_status: "active",
    learning_route: null,
    curriculum_pathway: null,
    preferred_delivery: null,
    supported_subjects: [],
    pathway_blocks: null,
  };
}

export async function createLearner(
  supabase: SupabaseClient,
  guardianId: string,
  input: LearnerProfileInput,
): Promise<LearnerSaveResult> {
  const payload = { ...input, guardian_id: guardianId, initials: initialsFor(input.name) };
  const write = (body: Record<string, unknown>, columns: string) =>
    supabase.from("homeschool_learners").insert(body).select(columns).single();

  const modern = await write(payload, LEARNER_SELECT);
  if (!modern.error) {
    const learner = modern.data as unknown as Learner;
    return { learner, request: await syncServiceRequest(supabase, learner) };
  }
  const withLevel = await write(withoutRouteFields(payload), LEARNER_LEVEL_SELECT);
  if (!withLevel.error) {
    return { learner: withLegacyRoute(withLevel.data as unknown as Learner), request: { kind: "none" } };
  }
  // Without 20260903000000 the level columns do not exist, and a parent adding
  // their first child must not be stopped by that.
  const legacy = unwrap(
    await write(withoutLevelFields(withoutRouteFields(payload)), LEARNER_BASE_SELECT),
  ) as unknown as Learner;
  return {
    learner: withLegacyRoute({ ...legacy, level_slug: null, level_started_on: null } as Learner),
    request: { kind: "none" },
  };
}

export async function updateLearner(
  supabase: SupabaseClient,
  learnerId: string,
  input: LearnerProfileInput,
): Promise<LearnerSaveResult> {
  const payload = {
    ...input,
    initials: initialsFor(input.name),
    updated_at: new Date().toISOString(),
  };
  const write = (body: Record<string, unknown>, columns: string) =>
    supabase.from("homeschool_learners").update(body).eq("id", learnerId).select(columns).single();

  const modern = await write(payload, LEARNER_SELECT);
  if (!modern.error) {
    const learner = modern.data as unknown as Learner;
    return { learner, request: await syncServiceRequest(supabase, learner) };
  }
  const withLevel = await write(withoutRouteFields(payload), LEARNER_LEVEL_SELECT);
  if (!withLevel.error) {
    return { learner: withLegacyRoute(withLevel.data as unknown as Learner), request: { kind: "none" } };
  }
  const legacy = unwrap(
    await write(withoutLevelFields(withoutRouteFields(payload)), LEARNER_BASE_SELECT),
  ) as unknown as Learner;
  return {
    learner: withLegacyRoute({ ...legacy, level_slug: null, level_started_on: null } as Learner),
    request: { kind: "none" },
  };
}

/**
 * How far a request has travelled decides whether a family may still rewrite it.
 *
 * Up to and including `submitted` it is still only a statement of what the
 * family wants, so an edit simply replaces it. From `matching` onwards a
 * coordinator is working it — teachers have been approached, a quotation may
 * have gone out — and silently rewriting it underneath them would lose that
 * work without anyone noticing.
 */
const FAMILY_EDITABLE_STATUSES = ["draft", "submitted"];

/** A saved learner, plus what the save did to their matching request. */
export type LearnerSaveResult = { learner: Learner; request: ServiceRequestOutcome };

/** What a save did to the matching request, so the form can say so. */
export type ServiceRequestOutcome =
  | { kind: "none" }
  | { kind: "created" }
  | { kind: "updated" }
  | { kind: "closed" }
  /** Left alone because the office is already working it. */
  | { kind: "locked"; status: string };

/**
 * Turn a learner's enrolment choice into programme work.
 *
 * One current request per learner is intentional: changing pathway or delivery
 * updates the request the matching team is already handling instead of opening
 * competing tickets. The unique index on learner_id enforces it.
 *
 * ── Why this reads before it writes ─────────────────────────────────────────
 * The obvious implementation is a single upsert, and it was one. Two things
 * went wrong with it, both on the ordinary path rather than an edge case:
 *
 *  1. Postgres does NOT silently skip the update half of an `on conflict do
 *     update` when the existing row fails the UPDATE policy's USING clause — it
 *     raises an error. The family policy only admits 'draft' and 'submitted',
 *     so the moment a coordinator moved a request to 'matching', every
 *     subsequent save of that child's profile failed. Not just a route change:
 *     ANY edit, because the sync ran on every update. The learner row had
 *     already been written by then, so the parent saw an error over a change
 *     that had in fact been saved.
 *  2. The upsert always sent `status: 'submitted'`, so had the policy admitted
 *     it, a parent correcting a spelling would have walked a quoted request
 *     back to submitted and lost the office's place in its own queue.
 *
 * Reading first settles both. A request the office has started is left exactly
 * as it is, and the caller is told, so the family can be asked to ring rather
 * than left believing a change went through.
 */
/** The existing request, as little of it as the decision needs. */
export type ExistingServiceRequest = { id: string; status: string } | null;

/** What ought to happen to the request, decided before anything is written. */
export type ServiceRequestPlan =
  | { action: "none" }
  | { action: "close"; id: string }
  | { action: "update"; id: string }
  /** Reuse a closed row: the unique index allows only one per learner. */
  | { action: "reopen"; id: string }
  | { action: "insert" }
  | { action: "locked"; status: string };

/**
 * Decide what a learner's saved choice should do to their matching request.
 *
 * Pure, and separate from the writing, because this is where both of the bugs
 * in the first version lived — and a decision with six outcomes and a status
 * machine behind it is worth being able to test without a database.
 */
export function planServiceRequest(
  current: ExistingServiceRequest,
  learner: Pick<
    Learner,
    "learning_route" | "curriculum_pathway" | "preferred_delivery"
  >,
): ServiceRequestPlan {
  const open = current && current.status !== "closed" ? current : null;
  // Anything the office has taken up is theirs until they release it.
  if (open && !FAMILY_EDITABLE_STATUSES.includes(open.status)) {
    return { action: "locked", status: open.status };
  }

  if (learner.learning_route === "parent_led") {
    return open ? { action: "close", id: open.id } : { action: "none" };
  }

  // An incomplete choice is not a request. The form blocks this; the data layer
  // does not rely on it having done so.
  if (!learner.learning_route || !learner.curriculum_pathway || !learner.preferred_delivery) {
    return { action: "none" };
  }

  if (open) return { action: "update", id: open.id };
  if (current) return { action: "reopen", id: current.id };
  return { action: "insert" };
}

async function syncServiceRequest(
  supabase: SupabaseClient,
  learner: Learner,
): Promise<ServiceRequestOutcome> {
  const existing = await supabase
    .from("homeschool_service_requests")
    .select("id,status")
    .eq("learner_id", learner.id)
    .maybeSingle();
  // Before the migration there is no table, and a parent adding their first
  // child must not be stopped by that.
  if (existing.error) {
    if (existing.error.code === "42P01") return { kind: "none" };
    throw new Error(existing.error.message);
  }

  const plan = planServiceRequest(existing.data as ExistingServiceRequest, learner);
  if (plan.action === "none") return { kind: "none" };
  if (plan.action === "locked") return { kind: "locked", status: plan.status };

  const requests = () => supabase.from("homeschool_service_requests");
  const now = new Date().toISOString();
  const details = {
    learning_route: learner.learning_route,
    curriculum_pathway: learner.curriculum_pathway,
    preferred_delivery: learner.preferred_delivery,
    supported_subjects: learner.supported_subjects,
    updated_at: now,
  };

  if (plan.action === "close") {
    const { error } = await requests().update({ status: "closed", updated_at: now }).eq("id", plan.id);
    if (error) throw new Error(error.message);
    return { kind: "closed" };
  }

  if (plan.action === "update") {
    // Status is deliberately NOT sent: it is already 'draft' or 'submitted',
    // and every value beyond those belongs to the office.
    const { error } = await requests().update(details).eq("id", plan.id);
    if (error) throw new Error(error.message);
    return { kind: "updated" };
  }

  if (plan.action === "reopen") {
    const { error } = await requests().update({ ...details, status: "submitted" }).eq("id", plan.id);
    if (error) throw new Error(error.message);
    return { kind: "created" };
  }

  const { error } = await requests().insert({
    ...details,
    learner_id: learner.id,
    guardian_id: learner.guardian_id,
    status: "submitted",
  });
  if (error) throw new Error(error.message);
  return { kind: "created" };
}

/**
 * Retire a learner rather than delete them.
 *
 * Their portfolio is years of a childhood, and `on delete cascade` reaches all
 * the way through evidence to the files behind it. A parent who is finished
 * with the programme wants to stop seeing the daily plan, not to destroy the
 * record — so the row is deactivated and the data stays readable.
 */
export async function archiveLearner(supabase: SupabaseClient, learnerId: string) {
  const { error } = await supabase
    .from("homeschool_learners")
    .update({ is_active: false, enrolment_status: "completed", updated_at: new Date().toISOString() })
    .eq("id", learnerId);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// The parent's own details
// ---------------------------------------------------------------------------

const GUARDIAN_SELECT =
  "profile_id,phone,city,country,approach,looking_for,how_heard,coach_sharing_default";

export async function fetchGuardianDetails(
  supabase: SupabaseClient,
  profileId: string,
): Promise<GuardianDetails | null> {
  const { data, error } = await supabase
    .from("homeschool_guardian_details")
    .select(GUARDIAN_SELECT)
    .eq("profile_id", profileId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as GuardianDetails | null) ?? null;
}

export async function saveGuardianDetails(
  supabase: SupabaseClient,
  profileId: string,
  input: Omit<GuardianDetails, "profile_id">,
): Promise<GuardianDetails> {
  return unwrap(
    await supabase
      .from("homeschool_guardian_details")
      .upsert(
        { ...input, profile_id: profileId, updated_at: new Date().toISOString() },
        { onConflict: "profile_id" },
      )
      .select(GUARDIAN_SELECT)
      .single(),
  );
}

// ---------------------------------------------------------------------------
// Curriculum
// ---------------------------------------------------------------------------

/**
 * Overlay a translation row on its English base, field by field.
 *
 * Field by field rather than row by row on purpose: a translation that has a
 * title but no evidence hint should show the Urdu title and the English hint,
 * not fall back wholesale to English. Empty strings count as missing, because
 * that is what a half-finished authoring pass leaves behind.
 */
function overlay<T extends object>(
  base: T,
  // Every translated column is nullable in the database, so the incoming shape
  // is looser than Partial<T> — the filter below is what keeps it honest.
  translation: Record<string, string | null> | undefined,
): T {
  if (!translation) return base;
  const merged = { ...base };
  for (const [key, value] of Object.entries(translation)) {
    if (typeof value === "string" && value.trim() !== "") {
      (merged as Record<string, unknown>)[key] = value;
    }
  }
  return merged;
}

export async function fetchCompetencies(
  supabase: SupabaseClient,
  locale = "en",
): Promise<Competency[]> {
  const base = unwrap<Competency[]>(
    await supabase
      .from("homeschool_competencies")
      .select("id,code,title,domain,stage,description,next_step,sort_order,is_active,standard_refs")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .returns<Competency[]>(),
  );
  if (locale === "en") return base;

  const translations = unwrap<CompetencyTranslation[]>(
    await supabase
      .from("homeschool_competency_translations")
      .select("competency_id,title,domain,description,next_step")
      .eq("locale", locale)
      .returns<CompetencyTranslation[]>(),
  );
  const byId = new Map(translations.map(({ competency_id, ...rest }) => [competency_id, rest]));
  return base.map((competency) => overlay(competency, byId.get(competency.id)));
}

type CompetencyTranslation = {
  competency_id: string;
  title: string | null;
  domain: string | null;
  description: string | null;
  next_step: string | null;
};

type ActivityTranslation = {
  activity_id: string;
  title: string | null;
  area: string | null;
  prompt: string | null;
  say_this: string | null;
  observe_for: string | null;
  evidence_hint: string | null;
  parent_move: string | null;
  child_move: string | null;
  what_to_do: string | null;
  story: string | null;
};

type ActivityRow = Omit<Activity, "competency_ids" | "resources" | "original"> & {
  homeschool_activity_competencies: { competency_id: string }[] | null;
  homeschool_activity_resources: ActivityResource[] | null;
};

/**
 * The original Supabase bucket.
 *
 * New uploads go to R2 (see src/lib/files.ts and worker/resources.ts). This
 * stays because rows written before that change point here, and re-uploading
 * somebody's worksheets to move them would have been work with no benefit —
 * `storage_provider` on each row says which store to ask.
 */
export const RESOURCE_BUCKET = "homeschool-resources";

const RESOURCE_COLUMNS =
  "id,activity_id,kind,title,url,storage_path,body,storage_provider,mime_type," +
  "size_bytes,file_name,is_example,note,sort_order";

/**
 * The activity library.
 *
 * `includeRetired` defaults to false for any caller that just wants the
 * curriculum. The app itself asks for everything, for two reasons: an admin has
 * to be able to see what they retired — otherwise the button is one-way and the
 * only route back is the SQL editor — and a plan generated this morning must
 * keep rendering if the activity in it is retired this afternoon. Retired rows
 * are excluded where it actually matters, in `chooseActivities`.
 */
export async function fetchActivities(
  supabase: SupabaseClient,
  locale = "en",
  includeRetired = false,
): Promise<Activity[]> {
  /**
   * The curriculum columns ship with 20260903000000, and this app's migrations
   * are applied by hand. Asking for a column that is not there yet does not
   * degrade — PostgREST fails the whole select, `unwrap` throws, and the
   * Promise.all in reloadFamily rejects, so the app loses its learners and its
   * activities too and renders nothing at all. That is exactly what happened
   * when this shipped ahead of its migration. So: ask for them, and fall back
   * to the old column list if they are missing.
   */
  const build = (withCurriculum: boolean, withTrack: boolean) => {
    let query = supabase
      .from("homeschool_activities")
      .select(
        "id,slug,title,area,stage," +
          (withCurriculum ? "level_slug,term_index,sequence_order," : "") +
          (withTrack ? "track,band," : "") +
          "icon,color,duration_minutes,prompt,say_this,observe_for," +
          "evidence_hint,parent_move,child_move,what_to_do,story,default_evidence_kind,is_active,sort_order," +
          "teaching_language," +
          "homeschool_activity_competencies(competency_id)," +
          `homeschool_activity_resources(${RESOURCE_COLUMNS})`,
      );
    if (!includeRetired) query = query.eq("is_active", true);
    // The client is untyped (no generated Database types), so an embedded
    // relation cannot be inferred from the select string. `returns` is the
    // supported way to name the shape rather than casting through `unknown`.
    return query.order("sort_order", { ascending: true }).order("id").returns<ActivityRow[]>();
  };

  // Three tiers, not two.
  //
  // track/band and level_slug arrive in different migrations, and migrations
  // here are applied by hand — so there is always a window where one has landed
  // and the other has not. Folding both into a single modern/legacy pair would
  // mean a missing `track` column threw away `level_slug` as well, dropping
  // every child back to the pre-curriculum stage shuffle. Each tier gives up
  // only what it has to.
  // Probe only the first page to discover the schema; use that same select for
  // every page. A later network failure must not return a truncated library.
  let withCurriculum = true;
  let withTrack = true;
  let result = await build(true, true).range(0, 499);
  if (result.error) {
    withTrack = false;
    result = await build(true, false).range(0, 499);
  }
  if (result.error) {
    withCurriculum = false;
    result = await build(false, false).range(0, 499);
  }
  const allRows = await readAllPages<ActivityRow>((from, to) =>
    from === 0 ? Promise.resolve(result) : build(withCurriculum, withTrack).range(from, to),
  );
  const rows = allRows.map((row) => ({
    ...row,
    // Absent entirely on the legacy select. Null puts the activity back on the
    // old stage pool rather than into a level that has no terms.
    level_slug: row.level_slug ?? null,
    term_index: row.term_index ?? null,
    sequence_order: row.sequence_order ?? null,
    // Before the enrichment migration every activity is core, which is exactly
    // how the app behaved before this existed.
    track: row.track ?? ("core" as const),
    band: row.band ?? null,
  }));
  const activities: Activity[] = rows.map(
    ({ homeschool_activity_competencies: links, homeschool_activity_resources: resources, ...activity }) => ({
      ...activity,
      competency_ids: (links ?? []).map((link) => link.competency_id),
      resources: [...(resources ?? [])].sort((a, b) => a.sort_order - b.sort_order),
    }),
  );
  if (locale === "en") return activities;

  const translations = await readAllPages<ActivityTranslation>((from, to) =>
    supabase
      .from("homeschool_activity_translations")
      .select("activity_id,title,area,prompt,say_this,observe_for,evidence_hint,parent_move,child_move,what_to_do,story")
      .eq("locale", locale)
      .order("activity_id")
      .range(from, to)
      .returns<ActivityTranslation[]>(),
  );
  const byId = new Map(translations.map(({ activity_id, ...rest }) => [activity_id, rest]));
  type ResourceTranslation = { id: string; title_ur: string | null; body_ur: string | null; note_ur: string | null };
  const resourcePage = (from: number, to: number) => supabase.from("homeschool_activity_resources")
    .select("id,title_ur,body_ur,note_ur").order("id").range(from, to).returns<ResourceTranslation[]>();
  const first = await resourcePage(0, 499);
  // Content migrations are applied separately. Only an absent optional column
  // permits fallback; permissions/network/later-page failures still surface.
  const resourceTranslations = isMissingTranslationColumns(first.error) ? [] : await readAllPages<ResourceTranslation>(
    (from, to) => from === 0 ? Promise.resolve(first) : resourcePage(from, to),
  );
  const byResource = new Map(resourceTranslations.map(row => [row.id, row]));
  return activities.map(activity => ({
    ...localizeActivity(activity, byId.get(activity.id)),
    resources: activity.resources.map(resource => {
      const row = byResource.get(resource.id);
      return row ? { ...resource, title: row.title_ur?.trim() || resource.title, body: row.body_ur?.trim() || resource.body, note: row.note_ur?.trim() || resource.note } : resource;
    }),
  }));
}

// ---------------------------------------------------------------------------
// Activity resources
// ---------------------------------------------------------------------------

/**
 * A usable URL for one resource, whichever store it lives in.
 *
 * Three cases, and the row says which: an external link needs nothing, an R2
 * object needs the Worker to sign it, and a file from before the R2 move needs
 * Supabase to. A text resource has no URL at all — its content is in `body`.
 */
export async function resolveResourceUrl(supabase: SupabaseClient, resource: ActivityResource) {
  if (resource.url) return resource.url;
  if (!resource.storage_path) return null;

  if (resource.storage_provider === "r2") {
    try {
      return await signKey(supabase, resource.storage_path);
    } catch {
      return null;
    }
  }

  const { data, error } = await supabase.storage
    .from(RESOURCE_BUCKET)
    .createSignedUrl(resource.storage_path, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}

/**
 * URLs for a whole set of resources at once.
 *
 * An activity guide that renders three pictures inline should ask the Worker
 * once, not three times. R2 keys are batched into a single signing call;
 * Supabase-era rows still go one at a time, because there are few of them and
 * `createSignedUrls` would need its own error handling for a path that is now
 * the exception rather than the rule.
 */
export async function resolveResourceUrls(
  supabase: SupabaseClient,
  resources: ActivityResource[],
): Promise<Record<string, string>> {
  const urls: Record<string, string> = {};
  const r2Keys: string[] = [];

  for (const resource of resources) {
    if (resource.url) {
      urls[resource.id] = resource.url;
    } else if (resource.storage_path && resource.storage_provider === "r2") {
      r2Keys.push(resource.storage_path);
    }
  }

  if (r2Keys.length > 0) {
    try {
      const signed = await signKeys(supabase, r2Keys);
      for (const resource of resources) {
        const path = resource.storage_path;
        if (path && resource.storage_provider === "r2" && signed[path]) {
          urls[resource.id] = signed[path];
        }
      }
    } catch {
      // A signing failure leaves those resources without a URL, which the row
      // renders as "unavailable". It must not blank the rest of the guide.
    }
  }

  await Promise.all(
    resources
      .filter((resource) => !urls[resource.id] && resource.storage_path && resource.storage_provider !== "r2")
      .map(async (resource) => {
        const url = await resolveResourceUrl(supabase, resource);
        if (url) urls[resource.id] = url;
      }),
  );

  return urls;
}

export type ResourceDraft = {
  kind: ResourceKind;
  title: string;
  note: string;
  /** A link, written text, or an uploaded file — exactly one, which the table enforces. */
  url: string;
  body: string;
  file: File | null;
  isExample: boolean;
};

/**
 * Attach a resource to an activity.
 *
 * The upload happens first and is undone if the row fails, because the reverse
 * order leaves a row pointing at a file that was never written — a broken link
 * in a parent's guide, which is worse than an orphaned object nobody can see.
 */
export async function addActivityResource(
  supabase: SupabaseClient,
  input: ResourceDraft & { activityId: string; createdBy: string; sortOrder: number },
) {
  const uploaded = input.file ? await uploadFile(supabase, input.file, "activities") : null;

  const { error } = await supabase.from("homeschool_activity_resources").insert({
    activity_id: input.activityId,
    kind: input.kind,
    title: input.title,
    ...contentColumns(input, uploaded),
    is_example: input.isExample,
    note: input.note || null,
    sort_order: input.sortOrder,
    created_by: input.createdBy,
  });

  if (error) {
    if (uploaded) await deleteFile(supabase, uploaded.key).catch(() => {});
    throw new Error(error.message);
  }
}

/**
 * Edit a resource in place.
 *
 * Replacing the file is optional: an admin fixing a typo in the title should
 * not have to re-upload a 12 MB video to do it. When a new file *is* given, the
 * old object is removed only after the row has been repointed, so a failed
 * update never deletes the file the row still names.
 */
export async function updateActivityResource(
  supabase: SupabaseClient,
  resource: ActivityResource,
  input: ResourceDraft,
) {
  const uploaded = input.file ? await uploadFile(supabase, input.file, "activities") : null;
  const columns = contentColumns(input, uploaded, resource);

  // The old object is orphaned by any edit that stops the row pointing at it —
  // a replacement upload, but also switching a picture to written text or to a
  // link. `storage_path` in the new columns is the single thing that says so.
  const abandonsFile =
    Boolean(resource.storage_path) &&
    "storage_path" in columns &&
    columns.storage_path !== resource.storage_path;

  const { error } = await supabase
    .from("homeschool_activity_resources")
    .update({
      kind: input.kind,
      title: input.title,
      ...columns,
      is_example: input.isExample,
      note: input.note || null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", resource.id);

  if (error) {
    if (uploaded) await deleteFile(supabase, uploaded.key).catch(() => {});
    throw new Error(error.message);
  }

  // Only after the row has been repointed, so a failed update never deletes the
  // file the row still names.
  if (abandonsFile) await removeStoredFile(supabase, resource).catch(() => {});
}

/**
 * The three mutually exclusive content columns, in the one place that decides
 * between them.
 *
 * The table's check constraint allows exactly one of `url`, `storage_path` and
 * `body`, so every one of these branches has to null the other two — doing that
 * at each call site is how they eventually disagree and a save starts failing
 * with a constraint violation the parent-facing UI cannot explain.
 */
function contentColumns(
  input: ResourceDraft,
  uploaded: { key: string; size: number; mimeType: string; fileName: string | null } | null,
  existing?: ActivityResource,
) {
  if (uploaded) {
    return {
      url: null,
      body: null,
      storage_path: uploaded.key,
      storage_provider: "r2" as const,
      mime_type: uploaded.mimeType,
      size_bytes: uploaded.size,
      file_name: uploaded.fileName,
    };
  }
  if (input.kind === "text") {
    return {
      url: null,
      storage_path: null,
      storage_provider: "r2" as const,
      mime_type: null,
      size_bytes: null,
      file_name: null,
      body: input.body.trim(),
    };
  }
  if (input.url.trim()) {
    return {
      url: input.url.trim(),
      storage_path: null,
      body: null,
      storage_provider: "r2" as const,
      mime_type: null,
      size_bytes: null,
      file_name: null,
    };
  }
  // Editing a file-backed resource without replacing the file: leave every
  // content column exactly as it was.
  if (existing?.storage_path) return {};
  throw new Error("A resource needs a link, some text, or a file.");
}

/** Remove the object behind a resource, from whichever store holds it. */
async function removeStoredFile(supabase: SupabaseClient, resource: ActivityResource) {
  if (!resource.storage_path) return;
  if (resource.storage_provider === "r2") {
    await deleteFile(supabase, resource.storage_path);
    return;
  }
  await supabase.storage.from(RESOURCE_BUCKET).remove([resource.storage_path]);
}

export async function removeActivityResource(supabase: SupabaseClient, resource: ActivityResource) {
  const { error } = await supabase
    .from("homeschool_activity_resources")
    .delete()
    .eq("id", resource.id);
  if (error) throw new Error(error.message);
  // The row is gone whatever happens next; a file that outlives it is tidiness,
  // not correctness, and must not surface as a failed delete to the admin.
  await removeStoredFile(supabase, resource).catch(() => {});
}

/** Move a resource up or down the list a parent reads. */
export async function reorderActivityResource(
  supabase: SupabaseClient,
  resources: ActivityResource[],
  resourceId: string,
  direction: -1 | 1,
) {
  const ordered = [...resources].sort((a, b) => a.sort_order - b.sort_order);
  const index = ordered.findIndex((resource) => resource.id === resourceId);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ordered.length) return;

  [ordered[index], ordered[target]] = [ordered[target], ordered[index]];

  // Rewrite the whole run rather than swapping two values: seeds and hand-added
  // rows can share a sort_order, and swapping two equal numbers does nothing.
  await Promise.all(
    ordered.map(async (resource, position) => {
      if (resource.sort_order === position) return;
      const { error } = await supabase
        .from("homeschool_activity_resources")
        .update({ sort_order: position })
        .eq("id", resource.id);
      if (error) throw new Error(error.message);
    }),
  );
}

// ---------------------------------------------------------------------------
// The daily plan
// ---------------------------------------------------------------------------

/**
 * A small deterministic hash, so a learner's day is stable.
 *
 * The plan has to be reproducible for a given learner and date: two devices
 * opening the app at the same moment must propose the same six activities, or
 * whichever one loses the insert race would show a day that does not match what
 * the other just saved. `Math.random()` cannot give that, and shuffling on the
 * server would mean an RPC for something this small.
 */
function hash(value: string) {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * The activities a given learner should meet on a given date.
 *
 * Retired activities are filtered here rather than at the fetch, so that a plan
 * already containing one keeps rendering while never gaining another.
 */
export function chooseActivities(
  activities: Activity[],
  learnerId: string,
  date: string,
  stage: string,
  /**
   * Where the child has reached. Absent — a learner not yet on a level, or a
   * level with nothing authored into it — falls back to the old stage shuffle,
   * which is worse but is never an empty day.
   */
  placement?: { levelSlug: string | null; termIndex: number; dayInTerm: number },
) {
  // Enrichment never competes for one of the six. It is offered beside the day
  // by chooseEnrichment below, and if it leaked into this pool it would both
  // displace a core activity and shift the introduce/revisit window that makes
  // the term work.
  const published = activities.filter(
    (activity) => activity.is_active && activity.track !== "enrichment",
  );
  const seed = `${learnerId}:${date}`;

  // The curriculum path: a window over the term's running order, introducing a
  // couple of new activities and revisiting what came just before. See
  // curriculum.ts for why this replaced drawing six at random from everything.
  if (placement?.levelSlug) {
    const pool = termActivities(published, placement.levelSlug, placement.termIndex);
    if (pool.length > 0) {
      return activitiesForDay(pool, placement.dayInTerm, seed).map((activity, index) => ({
        activity,
        sort_order: index,
      }));
    }
  }

  const eligible = published.filter((activity) => activity.stage === stage);
  // A learner on a stage with no authored activities still gets a day rather
  // than an empty screen.
  const pool = eligible.length >= ACTIVITIES_PER_DAY ? eligible : published;
  return [...pool]
    .sort((a, b) => hash(seed + a.id) - hash(seed + b.id))
    .slice(0, ACTIVITIES_PER_DAY)
    .map((activity, index) => ({ activity, sort_order: index }));
}

/**
 * Where enrichment plan items start counting from.
 *
 * Far enough above the six that no core sort order can ever collide with one,
 * and low enough to read at a glance in the table.
 */
export const ENRICHMENT_SORT_BASE = 100;

/**
 * The optional extras offered beside today's six.
 *
 * Returned separately from chooseActivities rather than appended to it, because
 * everything downstream treats that list as "the day": it drives the progress
 * ring, the done count and whether the day is finished. An optional activity
 * that made a parent's progress ring read 6/8 would not be optional in any
 * sense a family would recognise.
 */
export function chooseEnrichment(
  activities: Activity[],
  stage: string,
  placement?: { levelSlug: string | null; termIndex: number; dayInTerm: number },
  dayIndex?: number,
): Activity[] {
  const band = bandForLevel(placement?.levelSlug ?? null, stage);
  const pool = activities.filter(
    (activity) => activity.track === "enrichment" && activity.is_active && activity.band === band,
  );
  // Counted in days worked, like everything else here: a family that took a
  // fortnight off comes back to the next extras, not to a gap.
  return enrichmentForDay(pool, dayIndex ?? placement?.dayInTerm ?? 0);
}

/**
 * Which term and which day of it this child is on.
 *
 * A learner with no level — created before levels existed — is placed on the
 * middle level of their old stage, which is where the existing library was
 * migrated to. Better a sensible guess than dropping them back to the shuffle.
 */
function placementFor(
  learner: Learner,
  curriculum?: { levels: Level[]; terms: Term[]; daysWorked: number },
): { levelSlug: string | null; termIndex: number; dayInTerm: number } | undefined {
  if (!curriculum) return undefined;
  const slug =
    learner.level_slug ?? (learner.stage === "foundation" ? "nursery" : "class-1");
  const level = curriculum.levels.find((candidate) => candidate.slug === slug) ?? null;
  if (!level) return undefined;
  const levelTerms = curriculum.terms.filter((term) => term.level_id === level.id);
  const progress = levelProgress(curriculum.daysWorked, level, levelTerms);
  return { levelSlug: slug, termIndex: progress.termIndex, dayInTerm: progress.dayInTerm };
}

type PlanDayRow = Omit<PlanDay, "items"> & { homeschool_plan_items: PlanItem[] | null };

const PLAN_SELECT =
  "id,learner_id,plan_date,note," +
  "homeschool_plan_items(id,plan_day_id,activity_id,sort_order,status,completed_at)";

function toPlanDay(row: PlanDayRow): PlanDay {
  const { homeschool_plan_items: items, ...day } = row;
  return { ...day, items: [...(items ?? [])].sort((a, b) => a.sort_order - b.sort_order) };
}

async function readPlan(supabase: SupabaseClient, learnerId: string, date: string) {
  const { data, error } = await supabase
    .from("homeschool_plan_days")
    .select(PLAN_SELECT)
    .eq("learner_id", learnerId)
    .eq("plan_date", date)
    .returns<PlanDayRow[]>()
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? toPlanDay(data) : null;
}

/** Read an existing day without creating one. Used while a tutor-supported or
 * teacher-led enrolment is waiting for its timetable and entitlement. */
export async function fetchPlan(supabase: SupabaseClient, learnerId: string, date: string) {
  return readPlan(supabase, learnerId, date);
}

/**
 * Today's plan, generating it on first open.
 *
 * The unique constraint on (learner_id, plan_date) is the concurrency control:
 * if another device created the day a moment ago this insert fails with 23505
 * and we simply read theirs. That is why the failure path re-reads rather than
 * retrying the insert.
 */
export async function fetchOrCreatePlan(
  supabase: SupabaseClient,
  learner: Learner,
  date: string,
  activities: Activity[],
  /**
   * Where the child has reached, so the day comes from their term's running
   * order rather than a shuffle of the whole stage. Omitted — an older caller,
   * or a level that has nothing authored into it yet — falls back to the
   * shuffle, which is worse but never an empty day.
   */
  curriculum?: { levels: Level[]; terms: Term[]; daysWorked: number },
): Promise<PlanDay> {
  const existing = await readPlan(supabase, learner.id, date);
  if (existing?.items.length) return existing;

  const placement = placementFor(learner, curriculum);
  const chosen = chooseActivities(activities, learner.id, date, learner.stage, placement);
  if (chosen.length === 0) {
    throw new Error("There are no activities in the library yet.");
  }

  // The optional extras are written into the plan alongside the six.
  //
  // They could have been left out of the table and rendered from the picker on
  // every load, but a plan item is what a tick, a completed_at and a piece of
  // evidence all hang off — evidence rows point at plan_item_id. Keeping them
  // out would have meant a child could do the music activity and have no way to
  // record that they had. What keeps them optional is not their absence from
  // the table; it is that the UI counts only `track === 'core'` towards the day.
  //
  // Sort orders start at ENRICHMENT_SORT_BASE so they land after the six
  // whatever happens, and so a glance at the table says which is which.
  const extras = chooseEnrichment(activities, learner.stage, placement, curriculum?.daysWorked).map(
    (activity, index) => ({ activity, sort_order: ENRICHMENT_SORT_BASE + index }),
  );

  let day: Omit<PlanDay, "items"> | null = existing;
  if (!day) {
    const inserted = await supabase
      .from("homeschool_plan_days")
      .insert({ learner_id: learner.id, plan_date: date })
      .select("id,learner_id,plan_date,note")
      .single();

    if (inserted.error) {
      if (inserted.error.code === "23505") {
        const raced = await readPlan(supabase, learner.id, date);
        if (raced?.items.length) return raced;
        day = raced;
      }
      if (!day) throw new Error(inserted.error.message);
    } else {
      day = inserted.data as Omit<PlanDay, "items">;
    }
  }

  // A failed item write used to leave an empty day that every retry returned
  // forever. Repair it idempotently, including when two tabs fill it at once.
  unwrap<PlanItem[]>(
    await supabase
      .from("homeschool_plan_items")
      .upsert(
        [...chosen, ...extras].map(({ activity, sort_order }) => ({
          plan_day_id: day.id,
          activity_id: activity.id,
          sort_order,
        })),
        { onConflict: "plan_day_id,activity_id", ignoreDuplicates: true },
      )
      .select("id,plan_day_id,activity_id,sort_order,status,completed_at"),
  );

  const completed = await readPlan(supabase, learner.id, date);
  if (!completed?.items.length) throw new Error("The plan could not be completed. Please try again.");
  return completed;
}

export async function setPlanItemStatus(
  supabase: SupabaseClient,
  itemId: string,
  status: PlanStatus,
  /** When it was done, if not now — a tick recorded the next morning belongs to its own day. */
  completedAt?: string,
): Promise<PlanItem> {
  return unwrap(
    await supabase
      .from("homeschool_plan_items")
      .update({ status, completed_at: status === "done" ? (completedAt ?? new Date().toISOString()) : null })
      .eq("id", itemId)
      .select("id,plan_day_id,activity_id,sort_order,status,completed_at")
      .single(),
  );
}

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

type EvidenceRow = Omit<Evidence, "ratings"> & {
  homeschool_evidence_competencies: { competency_id: string; rating: MasteryLevel }[] | null;
};

const EVIDENCE_SELECT =
  "id,learner_id,activity_id,plan_item_id,kind,title,note,storage_path,domain," +
  "shared_with_coach,review_status,coach_feedback,reviewed_at,occurred_at,created_at," +
  "homeschool_evidence_competencies(competency_id,rating)";

export async function fetchEvidence(supabase: SupabaseClient, learnerId: string): Promise<Evidence[]> {
  const rows = unwrap<EvidenceRow[]>(
    await supabase
      .from("homeschool_evidence")
      .select(EVIDENCE_SELECT)
      .eq("learner_id", learnerId)
      .order("occurred_at", { ascending: false })
      .returns<EvidenceRow[]>(),
  );
  return rows.map(({ homeschool_evidence_competencies: ratings, ...evidence }) => ({
    ...evidence,
    ratings: ratings ?? [],
  }));
}

function extensionFor(file: File) {
  const fromName = file.name.includes(".") ? file.name.split(".").pop() : "";
  if (fromName && /^[a-z0-9]{1,5}$/i.test(fromName)) return fromName.toLowerCase();
  return file.type.split("/")[1]?.replace(/[^a-z0-9]/gi, "") || "bin";
}

export async function createEvidence(
  supabase: SupabaseClient,
  input: {
    learnerId: string;
    createdBy: string;
    activityId: string | null;
    planItemId: string | null;
    kind: EvidenceKind;
    title: string;
    note: string;
    domain: string | null;
    sharedWithCoach: boolean;
    competencyIds: string[];
    rating: MasteryLevel;
    file: File | null;
  },
): Promise<Evidence> {
  let storagePath: string | null = null;
  if (input.file) {
    // The first path segment is the learner id because that is what the storage
    // RLS policy authorises against (see the foundation migration).
    storagePath = `${input.learnerId}/${crypto.randomUUID()}.${extensionFor(input.file)}`;
    const upload = await supabase.storage
      .from(EVIDENCE_BUCKET)
      .upload(storagePath, input.file, { contentType: input.file.type, upsert: false });
    if (upload.error) throw new Error(upload.error.message);
  }

  const inserted = await supabase
    .from("homeschool_evidence")
    .insert({
      learner_id: input.learnerId,
      activity_id: input.activityId,
      plan_item_id: input.planItemId,
      kind: input.kind,
      title: input.title,
      note: input.note || null,
      storage_path: storagePath,
      domain: input.domain,
      shared_with_coach: input.sharedWithCoach,
      created_by: input.createdBy,
    })
    .select("id,learner_id,activity_id,plan_item_id,kind,title,note,storage_path,domain,shared_with_coach,review_status,coach_feedback,reviewed_at,occurred_at,created_at")
    .single();

  if (inserted.error) {
    // Do not leave an orphaned file behind if the row could not be written.
    if (storagePath) await supabase.storage.from(EVIDENCE_BUCKET).remove([storagePath]);
    throw new Error(inserted.error.message);
  }

  const evidence = inserted.data as Omit<Evidence, "ratings">;
  const ratings = input.competencyIds.map((competency_id) => ({
    evidence_id: evidence.id,
    competency_id,
    rating: input.rating,
  }));

  if (ratings.length > 0) {
    const linked = await supabase.from("homeschool_evidence_competencies").insert(ratings);
    if (linked.error) throw new Error(linked.error.message);
  }

  return {
    ...evidence,
    ratings: ratings.map(({ competency_id, rating }) => ({ competency_id, rating })),
  };
}

export async function deleteEvidence(supabase: SupabaseClient, evidence: Evidence) {
  const { error } = await supabase.from("homeschool_evidence").delete().eq("id", evidence.id);
  if (error) throw new Error(error.message);
  if (evidence.storage_path) {
    await supabase.storage.from(EVIDENCE_BUCKET).remove([evidence.storage_path]);
  }
}

/**
 * A short-lived URL for one evidence file.
 *
 * The bucket is private, so there is no permanent URL to store: children's
 * photos and voice notes are readable only through a link that expires.
 */
export async function signedEvidenceUrl(supabase: SupabaseClient, storagePath: string) {
  const { data, error } = await supabase.storage
    .from(EVIDENCE_BUCKET)
    .createSignedUrl(storagePath, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}

// ---------------------------------------------------------------------------
// Mastery
// ---------------------------------------------------------------------------

export async function fetchMastery(supabase: SupabaseClient, learnerId: string): Promise<MasteryRow[]> {
  return unwrap(
    await supabase
      .from("homeschool_mastery")
      .select("learner_id,competency_id,evidence_count,percent,level,last_evidence_at")
      .eq("learner_id", learnerId),
  );
}

// ---------------------------------------------------------------------------
// Authoring (admin and manager only; RLS enforces it regardless of the UI)
// ---------------------------------------------------------------------------

export async function saveCompetency(
  supabase: SupabaseClient,
  input: Partial<Competency> & { code: string; title: string; domain: string },
) {
  const payload = {
    code: input.code,
    title: input.title,
    domain: input.domain,
    stage: input.stage ?? "primary",
    description: input.description ?? null,
    next_step: input.next_step ?? null,
    sort_order: input.sort_order ?? 0,
    updated_at: new Date().toISOString(),
  };
  const query = input.id
    ? supabase.from("homeschool_competencies").update(payload).eq("id", input.id)
    : supabase.from("homeschool_competencies").insert(payload);
  const { error } = await query;
  if (error) throw new Error(error.message);
}

export async function saveActivity(
  supabase: SupabaseClient,
  input: Partial<Activity> & { slug: string; title: string; area: string; prompt: string },
  competencyIds: string[],
) {
  const payload = {
    slug: input.slug,
    title: input.title,
    area: input.area,
    stage: input.stage ?? "primary",
    level_slug: input.level_slug ?? null,
    term_index: input.term_index ?? null,
    sequence_order: input.sequence_order ?? null,
    icon: input.icon ?? "spark",
    color: input.color ?? "blue",
    duration_minutes: input.duration_minutes ?? 10,
    prompt: input.prompt,
    say_this: input.say_this ?? null,
    observe_for: input.observe_for ?? null,
    evidence_hint: input.evidence_hint ?? null,
    parent_move: input.parent_move ?? null,
    child_move: input.child_move ?? null,
    // Per-activity step 1 and the worked scene. Both nullable: an activity with
    // no what_to_do falls back to the generic sentence rather than an empty step.
    what_to_do: input.what_to_do ?? null,
    story: input.story ?? null,
    default_evidence_kind: input.default_evidence_kind ?? "observation",
    // The language the activity *teaches* in, which is not the language it is
    // read in. Omitting it here was why the editor could not correct an English
    // phonics activity that had been mislabelled at seed time.
    teaching_language: input.teaching_language ?? "any",
    sort_order: input.sort_order ?? 0,
    updated_at: new Date().toISOString(),
  };

  const saved = input.id
    ? await supabase.from("homeschool_activities").update(payload).eq("id", input.id).select("id").single()
    : await supabase.from("homeschool_activities").insert(payload).select("id").single();
  if (saved.error) throw new Error(saved.error.message);

  const activityId = (saved.data as { id: string }).id;

  // Replace the mapping wholesale. It is a two-column join table of at most a
  // handful of rows, so diffing it would be more code than it saves.
  const cleared = await supabase
    .from("homeschool_activity_competencies")
    .delete()
    .eq("activity_id", activityId);
  if (cleared.error) throw new Error(cleared.error.message);

  if (competencyIds.length > 0) {
    const { error } = await supabase
      .from("homeschool_activity_competencies")
      .insert(competencyIds.map((competency_id) => ({ activity_id: activityId, competency_id })));
    if (error) throw new Error(error.message);
  }
}

export async function setActivityActive(supabase: SupabaseClient, activityId: string, isActive: boolean) {
  const { error } = await supabase
    .from("homeschool_activities")
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq("id", activityId);
  if (error) throw new Error(error.message);
}

/**
 * Delete an activity for good, with its resources and their files.
 *
 * Retiring is almost always the right action and is what the library offers
 * first: a retired activity leaves every plan item and every piece of evidence
 * that points at it intact. Deleting is for the mistake — the duplicate, the
 * test row — and Postgres refuses it outright if any plan has ever used the
 * activity (`on delete restrict` on `homeschool_plan_items`). That refusal is a
 * feature, so it is translated into a sentence rather than suppressed.
 */
export async function deleteActivity(supabase: SupabaseClient, activity: Activity) {
  const { error } = await supabase.from("homeschool_activities").delete().eq("id", activity.id);
  if (error) {
    // 23503: a plan item still references it.
    if (error.code === "23503") {
      throw new Error(
        "A family has already had this activity in their day, so it cannot be deleted. Retire it instead — their record stays intact and it stops appearing in new plans.",
      );
    }
    throw new Error(error.message);
  }
  // The rows cascaded with the activity; the files behind them did not.
  await Promise.all(
    activity.resources.map((resource) => removeStoredFile(supabase, resource).catch(() => {})),
  );
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

const SESSION_SELECT =
  "id,learner_id,plan_day_id,plan_item_id,activity_id,source,title,minutes,note,occurred_at,recorded_by";

export async function fetchSessions(
  supabase: SupabaseClient,
  learnerId: string,
  limit = 200,
): Promise<Session[]> {
  return unwrap(
    await supabase
      .from("homeschool_sessions")
      .select(SESSION_SELECT)
      .eq("learner_id", learnerId)
      .order("occurred_at", { ascending: false })
      .limit(limit),
  );
}

/**
 * Bank the minutes for a completed activity.
 *
 * `upsert` on `plan_item_id` rather than `insert`, because the unique
 * constraint there is what stops a parent who ticks, un-ticks and re-ticks from
 * banking the same twenty minutes three times. Ignoring a duplicate is the
 * correct outcome, not an error to show anyone.
 */
export async function logActivitySession(
  supabase: SupabaseClient,
  input: {
    learnerId: string;
    planDayId: string;
    planItemId: string;
    activityId: string;
    title: string;
    minutes: number;
    recordedBy: string;
    /** Omitted = now. Set for a tick recorded after the day it happened. */
    occurredAt?: string;
  },
) {
  const { error } = await supabase.from("homeschool_sessions").upsert(
    {
      learner_id: input.learnerId,
      plan_day_id: input.planDayId,
      plan_item_id: input.planItemId,
      activity_id: input.activityId,
      source: "activity",
      title: input.title,
      minutes: input.minutes,
      recorded_by: input.recordedBy,
      ...(input.occurredAt ? { occurred_at: input.occurredAt } : {}),
    },
    { onConflict: "plan_item_id", ignoreDuplicates: true },
  );
  if (error) throw new Error(error.message);
}

/** Un-ticking an activity takes its minutes back out again. */
export async function removeActivitySession(supabase: SupabaseClient, planItemId: string) {
  const { error } = await supabase.from("homeschool_sessions").delete().eq("plan_item_id", planItemId);
  if (error) throw new Error(error.message);
}

/**
 * A session the app did not plan: a museum trip, an hour of reading, a lesson
 * with a tutor. Homeschooling is mostly this, and a record that only counted
 * what the app suggested would understate what the child actually did.
 */
export async function logManualSession(
  supabase: SupabaseClient,
  input: {
    learnerId: string;
    title: string;
    minutes: number;
    note: string;
    occurredAt: string;
    recordedBy: string;
  },
): Promise<Session> {
  return unwrap(
    await supabase
      .from("homeschool_sessions")
      .insert({
        learner_id: input.learnerId,
        source: "manual",
        title: input.title,
        minutes: input.minutes,
        note: input.note || null,
        occurred_at: input.occurredAt,
        recorded_by: input.recordedBy,
      })
      .select(SESSION_SELECT)
      .single(),
  );
}

export async function deleteSession(supabase: SupabaseClient, sessionId: string) {
  const { error } = await supabase.from("homeschool_sessions").delete().eq("id", sessionId);
  if (error) throw new Error(error.message);
}

/** Minutes per calendar day, newest first — what the reports page draws. */
export function minutesByDay(sessions: Session[], days: number) {
  const buckets = new Map<string, number>();
  const cutoff = Date.now() - days * 86_400_000;
  for (const session of sessions) {
    const at = new Date(session.occurred_at).getTime();
    if (Number.isNaN(at) || at < cutoff) continue;
    const day = today(new Date(at));
    buckets.set(day, (buckets.get(day) ?? 0) + session.minutes);
  }
  return buckets;
}

// ---------------------------------------------------------------------------
// Suggestions
// ---------------------------------------------------------------------------

const SUGGESTION_SELECT =
  "id,learner_id,activity_id,kind,title,body,url,storage_path,file_name,mime_type," +
  "status,admin_response,reviewed_at,created_by,created_at";

/**
 * Suggestions the caller may see.
 *
 * There is no `mine` flag and no admin variant: the RLS policy already says a
 * family sees their own and the programme sees them all, so one query serves
 * both and cannot drift from the rule it depends on.
 */
export async function fetchSuggestions(
  supabase: SupabaseClient,
  options: { status?: SuggestionStatus } = {},
): Promise<Suggestion[]> {
  let query = supabase.from("homeschool_suggestions").select(SUGGESTION_SELECT);
  if (options.status) query = query.eq("status", options.status);
  return unwrap(
    await query.order("created_at", { ascending: false }).limit(200).returns<Suggestion[]>(),
  );
}

export async function createSuggestion(
  supabase: SupabaseClient,
  input: {
    createdBy: string;
    learnerId: string | null;
    activityId: string | null;
    kind: SuggestionKind;
    title: string;
    body: string;
    url: string;
    file: File | null;
  },
): Promise<Suggestion> {
  const uploaded = input.file ? await uploadFile(supabase, input.file, "suggestions") : null;

  const inserted = await supabase
    .from("homeschool_suggestions")
    .insert({
      created_by: input.createdBy,
      learner_id: input.learnerId,
      activity_id: input.activityId,
      kind: input.kind,
      title: input.title,
      body: input.body,
      url: input.url.trim() || null,
      storage_path: uploaded?.key ?? null,
      file_name: uploaded?.fileName ?? null,
      mime_type: uploaded?.mimeType ?? null,
    })
    .select(SUGGESTION_SELECT)
    .returns<Suggestion[]>()
    .single();

  if (inserted.error) {
    if (uploaded) await deleteFile(supabase, uploaded.key).catch(() => {});
    throw new Error(inserted.error.message);
  }
  return inserted.data as Suggestion;
}

/** The programme's answer. Every reply is attributed and timestamped. */
export async function reviewSuggestion(
  supabase: SupabaseClient,
  suggestionId: string,
  input: { status: SuggestionStatus; response: string; reviewerId: string },
) {
  const { error } = await supabase
    .from("homeschool_suggestions")
    .update({
      status: input.status,
      admin_response: input.response.trim() || null,
      reviewed_by: input.reviewerId,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", suggestionId);
  if (error) throw new Error(error.message);
}

export async function withdrawSuggestion(supabase: SupabaseClient, suggestion: Suggestion) {
  // The file first: once the row is gone, RLS can no longer prove the caller
  // owned the object, and the Worker would rightly refuse to delete it.
  if (suggestion.storage_path) await deleteFile(supabase, suggestion.storage_path).catch(() => {});
  const { error } = await supabase.from("homeschool_suggestions").delete().eq("id", suggestion.id);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Review: what the programme owes families an answer on
// ---------------------------------------------------------------------------

/** Evidence with the learner's name attached, for the queue that spans families. */
export type EvidenceForReview = Evidence & { learner: { id: string; name: string } | null };

export async function fetchEvidenceForReview(
  supabase: SupabaseClient,
  status: Evidence["review_status"] | "all" = "pending",
): Promise<EvidenceForReview[]> {
  type Row = Omit<EvidenceForReview, "ratings" | "learner"> & {
    homeschool_evidence_competencies: { competency_id: string; rating: MasteryLevel }[] | null;
    homeschool_learners: { id: string; name: string } | null;
  };

  let query = supabase
    .from("homeschool_evidence")
    .select(`${EVIDENCE_SELECT},homeschool_learners(id,name)`);
  if (status !== "all") query = query.eq("review_status", status);

  const rows = unwrap<Row[]>(
    await query.order("created_at", { ascending: true }).limit(200).returns<Row[]>(),
  );
  return rows.map(
    ({ homeschool_evidence_competencies: ratings, homeschool_learners: learner, ...evidence }) => ({
      ...evidence,
      ratings: ratings ?? [],
      learner,
    }),
  );
}

/**
 * Approve a piece of evidence, or ask for something more.
 *
 * `changes_requested` is not a rejection and is not destructive: the mastery
 * view excludes it from the rolling average, so a rating given in error stops
 * counting the moment it is flagged, and starts counting again if it is later
 * approved. Nothing is deleted to achieve that.
 */
export async function reviewEvidence(
  supabase: SupabaseClient,
  evidenceId: string,
  input: { status: Evidence["review_status"]; feedback: string; reviewerId: string },
) {
  const { error } = await supabase
    .from("homeschool_evidence")
    .update({
      review_status: input.status,
      coach_feedback: input.feedback.trim() || null,
      reviewed_by: input.reviewerId,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", evidenceId);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// The programme's view of its families
// ---------------------------------------------------------------------------

const OVERVIEW_SELECT =
  "learner_id,name,stage,avatar_color,initials,date_of_birth,started_on,enrolment_status," +
  "weekly_target_minutes,guardian_id,guardian_name,guardian_email,evidence_count," +
  "pending_reviews,last_evidence_at,minutes_30d,active_days_30d,last_session_at," +
  "mastery_percent,secure_competencies";

/**
 * One row per learner the caller may see.
 *
 * The same call serves a parent and an admin — the view is `security_invoker`,
 * so RLS decides which learners come back. There is no separate admin endpoint
 * to keep in step.
 */
export async function fetchLearnerOverview(supabase: SupabaseClient): Promise<LearnerOverview[]> {
  return unwrap(
    await supabase
      .from("homeschool_learner_overview")
      .select(OVERVIEW_SELECT)
      .order("name", { ascending: true })
      .returns<LearnerOverview[]>(),
  );
}

// ---------------------------------------------------------------------------
// Matching: turning a family's choice into an arranged teacher
// ---------------------------------------------------------------------------

/**
 * Where a request has got to.
 *
 * `submitted` is the family's statement of what they want. Everything after it
 * is the office's, which is why the family's own RLS policy stops at
 * 'submitted' and why the app refuses to rewrite a request past that point.
 */
export type ServiceRequestStatus =
  | "draft"
  | "submitted"
  | "matching"
  | "quoted"
  | "accepted"
  | "closed";

/** The statuses a coordinator moves a request through, in order. */
export const SERVICE_REQUEST_FLOW: ServiceRequestStatus[] = [
  "submitted",
  "matching",
  "quoted",
  "accepted",
  "closed",
];

export type ServiceRequest = {
  id: string;
  learner_id: string;
  guardian_id: string;
  learning_route: "subject_support" | "teacher_led";
  curriculum_pathway: CurriculumPathway;
  preferred_delivery: DeliveryMode;
  supported_subjects: string[];
  /**
   * The one subject asked about, when a parent-led family wanted help with a
   * single subject. Null on a whole-programme request — and the difference is
   * the first thing a coordinator needs, because finding one Maths tutor is not
   * the same job as staffing a five-day timetable.
   */
  subject: string | null;
  /** Narrower still: one weekly block rather than the whole subject. */
  block_label: string | null;
  preferred_schedule: string | null;
  quoted_monthly_pkr: number | null;
  status: ServiceRequestStatus;
  created_at: string;
  updated_at: string;
};

const SERVICE_REQUEST_SELECT =
  "id,learner_id,guardian_id,learning_route,curriculum_pathway,preferred_delivery," +
  "supported_subjects,subject,block_label,preferred_schedule,quoted_monthly_pkr,status,created_at,updated_at";

/**
 * Every request the caller may see.
 *
 * RLS decides that: a coordinator sees all of them, a family only their own.
 * Oldest first, because the queue is worked in the order families joined it and
 * the one at risk of being forgotten is the one that has waited longest.
 */
export async function fetchServiceRequests(supabase: SupabaseClient): Promise<ServiceRequest[]> {
  const { data, error } = await supabase
    .from("homeschool_service_requests")
    .select(SERVICE_REQUEST_SELECT)
    .order("created_at", { ascending: true })
    .returns<ServiceRequest[]>();
  // Before the migration there is no table; the screen says so rather than
  // failing to render at all.
  if (error) {
    if (error.code === "42P01") return [];
    throw new Error(error.message);
  }
  return data ?? [];
}

/**
 * Move a request on, or record what was agreed.
 *
 * Only ever called from the coordinator's screen. A family's own writes go
 * through syncServiceRequest, which deliberately never sends `status`.
 */
export async function updateServiceRequest(
  supabase: SupabaseClient,
  id: string,
  patch: Partial<Pick<ServiceRequest, "status" | "quoted_monthly_pkr" | "preferred_schedule">>,
): Promise<ServiceRequest> {
  return unwrap(
    await supabase
      .from("homeschool_service_requests")
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select(SERVICE_REQUEST_SELECT)
      .single()
      .returns<ServiceRequest>(),
  );
}

/**
 * Ask for a tutor for one subject, or one block of it.
 *
 * ── Why this is not syncServiceRequest ──────────────────────────────────────
 * That function turns a learner's ROUTE into a request, and its whole job is to
 * keep exactly one request per child. This is the opposite: a parent-led family
 * may want help with Maths and, separately, with Science, and those are two
 * matches to two different tutors. The unique index now covers
 * (learner, subject) among open requests, so pressing the button twice for the
 * same subject cannot open a second ticket, while two different subjects can.
 *
 * `delivery` is asked at this point rather than at enrolment, because now there
 * is a real subject and a real person to arrange, which is when a family can
 * actually answer "online or at your home?".
 */
export async function requestSubjectTutor(
  supabase: SupabaseClient,
  learner: Learner,
  input: { subject: string; blockLabel?: string | null; delivery: DeliveryMode },
): Promise<{ ok: true } | { ok: false; error: string }> {
  const existing = await supabase
    .from("homeschool_service_requests")
    .select("id,status")
    .eq("learner_id", learner.id)
    .eq("subject", input.subject)
    .neq("status", "closed")
    .maybeSingle();
  if (existing.error && existing.error.code !== "42P01" && existing.error.code !== "PGRST116") {
    return { ok: false, error: existing.error.message };
  }
  // Already asked. Saying so is better than silently doing nothing, and better
  // than opening a duplicate the office has to reconcile.
  if (existing.data) return { ok: false, error: "ALREADY_REQUESTED" };

  const { error } = await supabase.from("homeschool_service_requests").insert({
    learner_id: learner.id,
    guardian_id: learner.guardian_id,
    // The kind of help, not the child's route: the learner stays parent_led.
    learning_route: "subject_support",
    curriculum_pathway: learner.curriculum_pathway ?? "balanced",
    preferred_delivery: input.delivery,
    supported_subjects: [input.subject],
    subject: input.subject,
    block_label: input.blockLabel ?? null,
    status: "submitted",
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/** Which subjects this child already has an open tutor request for. */
export async function fetchOpenTutorRequests(
  supabase: SupabaseClient,
  learnerId: string,
): Promise<{ subject: string; status: string; blockLabel: string | null }[]> {
  const { data, error } = await supabase
    .from("homeschool_service_requests")
    .select("subject,status,block_label")
    .eq("learner_id", learnerId)
    .neq("status", "closed");
  if (error) {
    if (error.code === "42P01") return [];
    throw new Error(error.message);
  }
  return (data ?? [])
    .filter((row: Record<string, unknown>) => typeof row.subject === "string")
    .map((row: Record<string, unknown>) => ({
      subject: String(row.subject),
      status: String(row.status),
      blockLabel: (row.block_label as string) ?? null,
    }));
}

export type TeacherOption = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  /**
   * Approved by the office for homeschool work.
   *
   * Homeschool teaching is younger children, a competency programme and a
   * parent in the room — not the same job as subject tuition — so it is an
   * explicit approval rather than something every tutor inherits. Unapproved
   * teachers still appear, because the office must be able to assign someone
   * in a hurry, but they are marked and sorted below.
   */
  homeschoolApproved: boolean;
};

/**
 * Teachers a coordinator can put in front of a family.
 *
 * Read straight from the LMS profiles table — the homeschool app and the main
 * site share one database, so there is no second teacher list to keep in step.
 * Admins and managers may select any profile; nobody else reaches this screen.
 *
 * The city is embedded rather than stored on the profile: a teacher's location
 * is `service_city_id`, a reference to the shared cities table, and there is no
 * `city` column to read. It matters for home-visiting matches, which cannot be
 * made across cities.
 */
export async function fetchTeacherOptions(supabase: SupabaseClient): Promise<TeacherOption[]> {
  const read = (columns: string) =>
    supabase
      .from("profiles")
      .select(columns)
      .eq("role", "teacher")
      .order("full_name", { ascending: true })
      .limit(500);

  // The approval column ships with 20260911200000. Asking for a column that is
  // not there fails the whole select, so fall back rather than leave the
  // matching screen with no teachers at all.
  let { data, error } = await read("id,full_name,email,phone,cities(name),homeschool_approved_at");
  if (error) ({ data, error } = await read("id,full_name,email,phone,cities(name)"));
  if (error) throw new Error(error.message);
  // A select built from a variable loses its row type, so the shape is asserted
  // once here rather than at every field below.
  const rows = (data ?? []) as unknown as Record<string, unknown>[];
  return rows.map((row) => {
    // PostgREST returns an embedded to-one as an object, but older versions and
    // some relationship shapes hand back a single-element array.
    const embedded = row.cities as { name?: unknown } | { name?: unknown }[] | null;
    const city = Array.isArray(embedded) ? embedded[0]?.name : embedded?.name;
    return {
      id: String(row.id),
      name: String(row.full_name ?? "Unnamed"),
      email: (row.email as string) ?? null,
      phone: (row.phone as string) ?? null,
      city: city == null ? null : String(city),
      homeschoolApproved: Boolean(row.homeschool_approved_at),
    };
  });
}

/**
 * Approve a teacher for homeschool work, or withdraw it.
 *
 * The same timestamp drives two things: which teachers this screen offers
 * first, and the badge on the public teachers page. One column so the two can
 * never disagree about who Al Noor has actually approved.
 */
export async function setHomeschoolApproved(
  supabase: SupabaseClient,
  teacherId: string,
  approved: boolean,
): Promise<void> {
  const { error } = await supabase
    .from("profiles")
    .update({ homeschool_approved_at: approved ? new Date().toISOString() : null })
    .eq("id", teacherId);
  if (error) throw new Error(error.message);
}

export type CoachAssignment = { coach_id: string; learner_id: string; assigned_at: string };

/** Who is already teaching these children. */
export async function fetchCoachAssignments(supabase: SupabaseClient): Promise<CoachAssignment[]> {
  const { data, error } = await supabase
    .from("homeschool_coach_assignments")
    .select("coach_id,learner_id,assigned_at")
    .returns<CoachAssignment[]>();
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Put a teacher on a child.
 *
 * This is the row the teacher's whole workspace hangs off — homeschool learner
 * RLS reads it to decide which children appear in a teacher's account — so
 * assigning here is what actually gives them access, not merely a note of
 * intent. Idempotent, because the obvious response to a slow click is another.
 */
export async function assignCoach(
  supabase: SupabaseClient,
  coachId: string,
  learnerId: string,
): Promise<void> {
  const { error } = await supabase
    .from("homeschool_coach_assignments")
    .upsert({ coach_id: coachId, learner_id: learnerId }, { onConflict: "coach_id,learner_id" });
  if (error) throw new Error(error.message);
}

/** Take a teacher off a child, and with it their access to the family record. */
export async function unassignCoach(
  supabase: SupabaseClient,
  coachId: string,
  learnerId: string,
): Promise<void> {
  const { error } = await supabase
    .from("homeschool_coach_assignments")
    .delete()
    .eq("coach_id", coachId)
    .eq("learner_id", learnerId);
  if (error) throw new Error(error.message);
}

/**
 * The teachers on one child, for the family's own curriculum screen.
 *
 * ── Why the name can be missing ─────────────────────────────────────────────
 * A family may read their own child's coach assignments, but `profiles` is
 * readable only by staff, the profile's owner, and a guardian's linked students
 * — so the embedded name comes back null for a parent. That is the correct
 * privacy answer and not something to route around from the client, so the
 * screen says a teacher is assigned without naming them rather than showing a
 * blank. Staff opening the same screen do see the name.
 */
export async function fetchLearnerTeachers(
  supabase: SupabaseClient,
  learnerId: string,
): Promise<{ id: string; name: string | null }[]> {
  const { data, error } = await supabase
    .from("homeschool_coach_assignments")
    .select("coach_id,profiles(full_name)")
    .eq("learner_id", learnerId);
  if (error) {
    if (error.code === "42P01") return [];
    throw new Error(error.message);
  }
  return (data ?? []).map((row: Record<string, unknown>) => {
    const embedded = row.profiles as { full_name?: unknown } | { full_name?: unknown }[] | null;
    const name = Array.isArray(embedded) ? embedded[0]?.full_name : embedded?.full_name;
    return { id: String(row.coach_id), name: name == null ? null : String(name) };
  });
}

/** Every learner, including retired ones — the admin roster, not a family's list. */
export async function fetchAllLearners(supabase: SupabaseClient): Promise<Learner[]> {
  return unwrap(
    await supabase
      .from("homeschool_learners")
      .select(LEARNER_SELECT)
      .order("created_at", { ascending: false })
      .returns<Learner[]>(),
  );
}

export async function fetchLearnerNotes(
  supabase: SupabaseClient,
  learnerId: string,
): Promise<LearnerNote[]> {
  return unwrap(
    await supabase
      .from("homeschool_learner_notes")
      .select("id,learner_id,author_id,body,created_at")
      .eq("learner_id", learnerId)
      .order("created_at", { ascending: false }),
  );
}

export async function addLearnerNote(
  supabase: SupabaseClient,
  input: { learnerId: string; authorId: string; body: string },
): Promise<LearnerNote> {
  return unwrap(
    await supabase
      .from("homeschool_learner_notes")
      .insert({ learner_id: input.learnerId, author_id: input.authorId, body: input.body })
      .select("id,learner_id,author_id,body,created_at")
      .single(),
  );
}

export async function deleteLearnerNote(supabase: SupabaseClient, noteId: string) {
  const { error } = await supabase.from("homeschool_learner_notes").delete().eq("id", noteId);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Levels and terms
// ---------------------------------------------------------------------------

/** Playgroup through Class 5, in order. */
export async function fetchLevels(supabase: SupabaseClient): Promise<Level[]> {
  // Empty, never thrown. This table ships with 20260903000000 and the app has
  // to keep working before it is applied — an unlevelled child simply gets the
  // old stage pool and no progress banner.
  const { data, error } = await supabase
    .from("homeschool_levels")
    .select("id,slug,title,subtitle,stage,sort_order,days_total,is_active")
    .eq("is_active", true)
    .order("sort_order", { ascending: true })
    .returns<Level[]>();
  if (error) return [];
  return data ?? [];
}

type TermRow = Omit<Term, "goals"> & { goals: unknown };

/**
 * Every term of every level.
 *
 * All of them in one read rather than per level: there are twenty-four rows in
 * total and the app needs a child's terms on Today, on Goals and on the level
 * picker, so paying for three round-trips to avoid twenty rows would be a
 * strange trade.
 */
export async function fetchTerms(supabase: SupabaseClient): Promise<Term[]> {
  const { data, error } = await supabase
    .from("homeschool_terms")
    .select("id,level_id,term_index,title,goal_summary,goals,start_day,end_day")
    .order("term_index", { ascending: true })
    .returns<TermRow[]>();
  // Same rule as fetchLevels: no terms is a missing migration, not a crash.
  if (error) return [];
  return (data ?? []).map((row) => ({
    ...row,
    // jsonb arrives as unknown; anything that is not a list of strings is
    // treated as no goals rather than rendered as [object Object].
    goals: Array.isArray(row.goals) ? row.goals.filter((goal): goal is string => typeof goal === "string") : [],
  }));
}

/**
 * How many days this child has actually worked.
 *
 * Days WORKED, not days elapsed. A plan day counts once at least one activity
 * on it is done, so opening the app and closing it again does not spend a day
 * of the year, and a fortnight away over Eid does not put a family "behind".
 */
export async function countDaysWorked(supabase: SupabaseClient, learnerId: string): Promise<number> {
  return (await fetchWorkedDates(supabase, learnerId)).length;
}

/**
 * The dates this child worked — the same rule as countDaysWorked, as a list, so
 * the streak and the year's progress are counted from one read.
 *
 * Empty, never thrown: a progress number is not worth a blank screen.
 */
export async function fetchWorkedDates(supabase: SupabaseClient, learnerId: string): Promise<string[]> {
  try {
    const rows = await readAllPages<{ id: string; plan_date: string }>((from, to) =>
      supabase
        .from("homeschool_plan_days")
        .select("id,plan_date,homeschool_plan_items!inner(status)")
        .eq("learner_id", learnerId)
        .eq("homeschool_plan_items.status", "done")
        .order("plan_date", { ascending: false })
        .range(from, to),
    );
    // One row per day that has any done item; distinct anyway, because a day
    // with four completed activities must still count as one day.
    return [...new Set(rows.map((row) => row.plan_date))];
  } catch {
    return [];
  }
}

/**
 * Plans from `from` up to (not including) `before`, newest first. Read-only:
 * this never creates a day, so looking back can't invent one.
 */
export async function fetchRecentPlans(
  supabase: SupabaseClient,
  learnerId: string,
  from: string,
  before: string,
): Promise<PlanDay[]> {
  const { data, error } = await supabase
    .from("homeschool_plan_days")
    .select(PLAN_SELECT)
    .eq("learner_id", learnerId)
    .gte("plan_date", from)
    .lt("plan_date", before)
    .order("plan_date", { ascending: false })
    .returns<PlanDayRow[]>();
  if (error) throw new Error(error.message);
  return (data ?? []).map(toPlanDay);
}
